/**
 * Styles ship as a string, not a `.css` import, so the embed is a single JS file that drops into
 * any host build without touching its bundler config. Every rule is scoped under `.ec-root`, and
 * every colour, radius, and font is a custom property a host can override.
 *
 * The tokens are exported on their own (`eventCreatorTokensCss`) so a host can apply the same
 * palette to its own chrome — that's how the hosted site stays visually one product with the
 * component instead of two designs sharing a page. One definition, two scopes.
 */

/**
 * The tokens are *generated* from the shared palette (`@project/event-creator`), so the web and
 * native surfaces can't drift: there is one blue, one grey, one radius scale, in one file.
 */
import {
  DARK_PALETTE,
  LIGHT_PALETTE,
  RADIUS,
  type EventCreatorPalette,
} from '@project/event-creator';

const kebab = (name: string): string => name.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);

function paletteDeclarations(palette: EventCreatorPalette): string {
  return Object.entries(palette)
    .map(([name, value]) => `  --ec-${kebab(name)}: ${value};`)
    .join('\n');
}

/**
 * The chevron a `<select>` draws for itself once we've turned its native appearance off (that
 * appearance is what made Safari round the control its own way instead of ours). It's generated
 * from the palette like every other token, so the arrow is the same grey as the labels in both
 * schemes rather than a hard-coded one that only suits light mode.
 */
function caretDeclaration(palette: EventCreatorPalette): string {
  const stroke = encodeURIComponent(palette.muted);
  return `  --ec-caret: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 8'%3E%3Cpath d='M1 1.5 6 6.5 11 1.5' fill='none' stroke='${stroke}' stroke-width='1.75' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");`;
}

const SHARED_TOKENS = `
  /* The widest the creator lets itself get before it centres in the space it's given. Past
     this, fields keep growing but nothing reads better — a 540px-wide text input is harder to
     scan, not easier — and the short side column leaves a void on one side. A host that wants
     it edge-to-edge can raise this like any other token. */
  --ec-max-width: 860px;
  --ec-radius: ${RADIUS.field}px;
  --ec-radius-lg: ${RADIUS.card}px;
  --ec-shadow: 0 1px 2px rgb(16 24 40 / 6%);
  --ec-font: system-ui, -apple-system, "Segoe UI", sans-serif;
  --ec-mono: ui-monospace, SFMono-Regular, Menlo, monospace;
`;

const LIGHT_TOKENS = `\n${paletteDeclarations(LIGHT_PALETTE)}\n${caretDeclaration(LIGHT_PALETTE)}\n${SHARED_TOKENS}`;
const DARK_TOKENS = `\n${paletteDeclarations(DARK_PALETTE)}\n${caretDeclaration(DARK_PALETTE)}\n  color-scheme: dark;\n`;

/**
 * The palette at a given scope, light and dark. Pass `:root` to theme a whole page with the
 * component's colours; the component itself uses `.ec-root` so an embed never leaks into a host.
 */
export function eventCreatorTokensCss(selector: string): string {
  return `${selector} {${LIGHT_TOKENS}}
@media (prefers-color-scheme: dark) {
  ${selector} {${DARK_TOKENS}}
}`;
}

export const EVENT_CREATOR_CSS = `
${eventCreatorTokensCss('.ec-root')}
.ec-root {
  color: var(--ec-fg);
  background: var(--ec-bg);
  font-family: var(--ec-font);
  font-size: 15px;
  line-height: 1.45;
  display: grid;
  gap: 20px;
  /* minmax(0, …) — without it a long preview line makes the whole grid overflow its host. */
  grid-template-columns: minmax(0, 1fr);
  /* Centre in whatever container it's handed: a wide card, a wide embed host, the page. */
  max-width: var(--ec-max-width);
  margin-inline: auto;
}
/* Fallback for browsers without container queries (Safari < 16, Firefox < 110). */
@supports not (container-type: inline-size) {
  @media (min-width: 860px) {
    .ec-root[data-preview='on'] { grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr); }
  }
}
/* minmax(0, 1fr), not the default auto: a <select> is sized by its widest option in WebKit, so
   without this the time-zone list pushes the whole form wider than its container (it overflowed
   a 320px viewport by 12px). The fields track the container; the option text scrolls. */
.ec-fields { display: grid; gap: 14px; align-content: start; grid-template-columns: minmax(0, 1fr); }
/* Generic rows wrap; compact date/time and duration groups below opt into their own one-line
   grids instead of inheriting the old full-width-field behaviour. */
.ec-row { display: flex; flex-wrap: wrap; gap: 10px; }
.ec-row > .ec-field { flex: 1 1 200px; }
/* Two controls whose *values* are short — "None", "Busy" — rather than two that need room to be
   read. 200px of basis would stack them on a phone for no reason: nothing in either list is wider
   than the ~140px they get when they share a 320px screen, so they share it. Text inputs are not
   in here on purpose; a 140px title box is worse than a tall form. */
.ec-row-tight > .ec-field { flex: 1 1 130px; }
/* A pair is one side of the event — its date, its time, and the zone that time is in — and it
   wraps as a unit. 320px of basis, not 200: at 200 two pairs squeeze side by side in a 490px
   column and every field inside them stacks, which turns the row into two narrow columns. At 320
   the column gives one side per line with its own fields side by side, and a wide container
   still puts both sides up together.

   align-content: start because the two pairs are different heights whenever only one carries a
   zone — the default stretch spreads the shorter pair's rows to match the taller one, and the
   dates stop lining up. */
.ec-row > .ec-pair {
  display: grid;
  align-content: start;
  gap: 10px;
  flex: 1 1 320px;
  min-width: 0;
}
.ec-date-time {
  display: grid;
  /* Safari keeps a native calendar/clock button inside these controls and paints it over
     neighbouring text when the track is narrower than its intrinsic control width. Keep both
     tracks above that floor; the container rule below stacks them only when the whole side cannot
     accommodate the safe minimum. */
  grid-template-columns: minmax(150px, 1fr) minmax(140px, 1fr);
  gap: 10px;
  min-width: 0;
}
.ec-date-time > .ec-field:only-child { grid-column: 1 / -1; }
.ec-time-control { display: flex; align-items: center; gap: 6px; min-width: 0; }
.ec-time-control input { flex: 1 1 auto; min-width: 0; }
.ec-time-offset {
  flex: 0 0 auto;
  color: var(--ec-muted);
  font-size: 12px;
  line-height: 1;
  white-space: nowrap;
}
.ec-zone-field { min-width: 0; }
/* An all-day event has no time and no zone, so each pair is a lone date and 320px of basis would
   push the two dates onto separate lines for no reason. Engines without :has (Safari < 15.4) get
   the stacked version, which is only a little roomier. */
.ec-row > .ec-pair:has(.ec-date-time > .ec-field:only-child) { flex-basis: 200px; }
/* Grouped sections, the way a calendar app groups an event: what it is, when it is, who is
   coming, what warns you, the details. A real <fieldset>, so the grouping is announced rather
   than merely drawn, with the browser's default border and padding cleared away. */
.ec-group {
  display: grid;
  gap: 14px;
  min-width: 0;
  border: 0;
  margin: 0;
  padding: 0;
}
.ec-group + .ec-group { margin-top: 6px; padding-top: 18px; border-top: 1px solid var(--ec-border); }
.ec-group > legend {
  padding: 0;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--ec-muted);
}
/* A legend is out of the grid flow, so the gap above the first field has to come from here. */
.ec-group > legend + * { margin-top: 10px; }
/* An optional group, folded away until it is wanted (see Optional in EventCreator.tsx). Block
   rather than the grid its siblings use: display on a <details> is not honoured everywhere, and
   a summary that lost its row would be a broken control rather than a slightly tighter one. The
   fields inside get the grid instead, so the gaps match every other group. */
.ec-optional { display: block; }
.ec-group-body { display: grid; gap: 14px; margin-top: 12px; min-width: 0; }
/* The summary is the legend for the group it opens, so it is drawn as one — plus a marker, and a
   44px row, because on a phone this is the control that has to be hit before anything inside it
   can be. */
.ec-optional > summary {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--ec-muted);
  cursor: pointer;
  /* The native marker goes: a flex summary drops it in WebKit anyway, so drawing our own is what
     keeps the three engines showing the same control. It is the select's caret, rotated. */
  list-style: none;
}
.ec-optional > summary::-webkit-details-marker { display: none; }
.ec-optional > summary::before {
  content: '';
  flex: 0 0 auto;
  width: 12px;
  height: 8px;
  background: var(--ec-caret) no-repeat center / 12px 8px;
  transform: rotate(-90deg);
}
.ec-optional[open] > summary::before { transform: none; }
.ec-optional > summary:focus-visible { outline: 2px solid var(--ec-accent); outline-offset: 2px; }
/* What a closed group is holding. Right-aligned and in sentence case: it is the answer to "is
   there anything in there", not a second heading. */
.ec-tally {
  margin-left: auto;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
}
/* The event in one sentence, immediately above the buttons it informs. */
.ec-recap {
  margin: 0;
  padding: 10px 12px;
  border-radius: var(--ec-radius);
  background: var(--ec-surface);
  color: var(--ec-fg);
  font-size: 14px;
  overflow-wrap: anywhere;
}
.ec-switch { min-height: 44px; }
.ec-recap strong { display: block; font-size: 18px; }
.ec-recap p { margin: 6px 0 0; }
.ec-technical-preview summary { min-height: 44px; cursor: pointer; overflow-wrap: anywhere; }
.ec-field { display: grid; gap: 5px; min-width: 0; }
.ec-field > label { font-size: 13px; font-weight: 600; color: var(--ec-muted); }
.ec-autocomplete { position: relative; min-width: 0; }
.ec-autocomplete-list {
  position: absolute;
  z-index: 20;
  inset-inline: 0;
  max-height: 300px;
  overflow-y: auto;
  margin: 4px 0 0;
  padding: 4px 0;
  list-style: none;
  background: var(--ec-bg);
  border: 1px solid var(--ec-border);
  border-radius: var(--ec-radius);
  box-shadow: var(--ec-shadow);
}
.ec-autocomplete-option {
  min-height: 44px;
  display: flex;
  align-items: center;
  padding: 8px 10px;
  color: inherit;
  cursor: pointer;
}
.ec-autocomplete-option[data-active='true'], .ec-autocomplete-option:hover {
  background: var(--ec-accent-soft);
}
.ec-autocomplete-empty { padding: 10px; color: var(--ec-muted); }
.ec-root input[type='text'],
.ec-root input[type='date'],
.ec-root input[type='time'],
.ec-root input[type='email'],
.ec-root input[type='number'],
.ec-root input[type='url'],
.ec-root select,
.ec-root textarea {
  font-family: inherit;
  /* 16px, not inherit: iOS Safari zooms the page when a focused field is smaller. It has to
     live on this selector — the shorthand here would otherwise out-specify a later rule. */
  font-size: 16px;
  line-height: 1.35;
  color: inherit;
  background: var(--ec-bg);
  border: 1px solid var(--ec-border);
  border-radius: var(--ec-radius);
  padding: 9px 10px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
}
/* WebKit sizes a <select> to its widest option and ignores min-width:0 when doing it, so the
   time-zone list widened the page until the element was capped explicitly. text-overflow keeps
   the closed control readable when a long zone name doesn't fit.

   appearance:none is what makes the dropdown a rounded rectangle like the text fields: a native
   select paints its own corners and ignores border-radius, so the two controls sat side by side
   with different silhouettes. Dropping the native look means drawing the chevron ourselves, and
   reserving room for it on the right so a long value never runs underneath it. */
.ec-root select {
  appearance: none;
  -webkit-appearance: none;
  max-width: 100%;
  text-overflow: ellipsis;
  background-image: var(--ec-caret);
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 12px 8px;
  padding-right: 30px;
}
.ec-root textarea { min-height: 76px; resize: vertical; }
.ec-root input:focus-visible, .ec-root select:focus-visible, .ec-root textarea:focus-visible {
  outline: 2px solid var(--ec-accent);
  outline-offset: 1px;
}
.ec-inline { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
/* The guest field and its Add button share a row: the input flexes, the button keeps its size,
   and both wrap to their own lines only when the container is genuinely too narrow. */
.ec-inline input[type='email'] { flex: 1 1 160px; width: auto; }
.ec-inline .ec-chip { flex: 0 0 auto; }
/* "Every [2] weeks" — the number keeps its own width instead of stretching across the row, and
   the unit sits beside it as text rather than pretending to be a second control. A count field
   on its own line is capped for the same reason: a four-digit answer in a 480px box reads as a
   mistake. */
.ec-inline input[type='number'] { flex: 0 0 88px; width: auto; }
.ec-field > input[type='number'] { max-width: 140px; }
/* Three boxes that mean one thing: grouped like a field, sized like the number inputs they are.
   The browser's fieldset border and padding are cleared, the same way .ec-group clears them. */
.ec-duration { display: grid; gap: 5px; min-width: 0; border: 0; margin: 0; padding: 0; }
.ec-duration > legend { padding: 0; font-size: 13px; font-weight: 600; color: var(--ec-muted); }
/* A legend is out of the grid flow, so the gap under it has to come from here. */
.ec-duration > legend + * { margin-top: 5px; }
.ec-duration .ec-inline { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); }
.ec-duration-part { min-width: 0; }
.ec-duration-part > label { font-size: 12px; font-weight: 400; }
.ec-duration .ec-inline input[type='number'] { width: 100%; max-width: none; }
.ec-unit { color: var(--ec-muted); font-size: 14px; }
/* Not bold: the mark says "don't empty this", and a red bold star on five of fourteen labels
   reads as five errors on a form nobody has touched yet. */
.ec-required { margin-left: 2px; color: var(--ec-danger); }
.ec-problem { color: var(--ec-danger); font-size: 13px; }
.ec-hint { margin: 0; color: var(--ec-muted); font-size: 12px; }
.ec-chip {
  border: 1px solid var(--ec-border);
  background: var(--ec-bg);
  color: inherit;
  border-radius: 999px;
  /* 44px is the smallest comfortable touch target on both iOS and Android guidance. */
  min-height: 44px;
  padding: 5px 14px;
  font: inherit;
  font-size: 13px;
  cursor: pointer;
}
.ec-chip[aria-pressed='true'] { border-color: var(--ec-accent); color: var(--ec-accent); background: var(--ec-accent-soft); }
.ec-attendee { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
/* The × beside a guest. Bare text is a ~20px target next to the address it deletes, which on a
   phone is a mis-tap away from removing the wrong person. */
.ec-remove { min-height: 44px; padding: 0 8px; border: 0; background: none; color: var(--ec-muted); cursor: pointer; font: inherit; }
.ec-actions { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; }
.ec-button {
  font: inherit;
  min-height: 44px;
  font-weight: 600;
  border: 1px solid var(--ec-accent);
  background: var(--ec-accent);
  color: var(--ec-accent-fg);
  border-radius: var(--ec-radius);
  padding: 10px 16px;
  cursor: pointer;
}
.ec-button.ec-secondary { background: var(--ec-bg); color: var(--ec-accent); }
/* A host collecting a calendar gets one persistent primary action. */
.ec-collect-action {
  position: sticky;
  bottom: 12px;
  z-index: 4;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  padding: 14px;
  background: var(--ec-bg);
  border: 1px solid var(--ec-border);
  border-radius: var(--ec-radius);
  box-shadow: 0 4px 20px rgb(16 24 40 / 12%);
}
.ec-collect-action > div { flex: 1 1 180px; min-width: 0; overflow-wrap: anywhere; }
.ec-collect-action .ec-hint { margin: 4px 0 0; }
.ec-collect-action .ec-button { flex: 1 0 auto; }
.ec-export-options > summary {
  min-height: 44px;
  display: list-item;
  align-content: center;
  color: var(--ec-muted);
  cursor: pointer;
}
.ec-export-options > summary:focus-visible { outline: 2px solid var(--ec-accent); outline-offset: 3px; }
.ec-export-options[open] > summary { margin-bottom: 14px; }
.ec-error-summary { color: var(--ec-danger); padding-inline-start: 20px; margin: 0; }
.ec-export-settings { margin-top: 14px; }
.ec-export-settings > summary { cursor: pointer; min-height: 44px; align-content: center; color: var(--ec-muted); }
.ec-export-settings[open] > summary { margin-bottom: 12px; }
.ec-export-settings > .ec-button { margin-top: 12px; }
.ec-side { display: grid; gap: 14px; align-content: start; }
.ec-panel { background: var(--ec-surface); border: 1px solid var(--ec-border); border-radius: var(--ec-radius); padding: 14px; }
.ec-panel h2 { margin: 0 0 8px; font-size: 13px; text-transform: uppercase; letter-spacing: 0.04em; color: var(--ec-muted); }
.ec-preview { margin: 0; overflow-wrap: anywhere; font-family: var(--ec-mono); font-size: 12px; line-height: 1.5; white-space: pre-wrap; word-break: break-word; max-height: 340px; overflow: auto; }
/* On a phone these four links are the whole point of the panel — they open the event in the
   calendar app without a file changing hands at all — so each is a row you can hit, not a line
   of text stacked 6px from the next one. */
.ec-links { display: grid; gap: 2px; }
.ec-links a { display: flex; align-items: center; min-height: 44px; color: var(--ec-accent); }
.ec-attendee span { overflow-wrap: anywhere; }
.ec-describe textarea { min-height: 66px; }
.ec-describe .ec-actions { margin-top: 8px; }
.ec-assumptions { margin: 10px 0 0; padding-left: 18px; color: var(--ec-muted); font-size: 13px; }
@media (forced-colors: active) {
  .ec-chip[aria-pressed='true'] { border: 2px solid; }
}
`;

/**
 * Container queries live in their own sheet, appended only where the engine understands them.
 * A parser that chokes on `@container` (jsdom, older engines) would otherwise drop the *whole*
 * stylesheet, so the layout above must stay parseable everywhere; the `@supports not` rule in
 * it is the fallback those engines get.
 */
export const EVENT_CREATOR_CONTAINER_CSS = `
/* The shell exists purely to be the query container: an element can't answer a container
   query about itself, so the width question is asked one level up. Container-relative rather
   than viewport-relative because an embed can sit in a narrow column on a wide screen, where
   a viewport query would hand it a two-column layout it has no room for. */
.ec-shell { container-type: inline-size; }
/* Narrow enough that three side-by-side buttons each end up a couple of words wide: give them a
   row each instead, primary first. Container-relative like the rule below — an embed in a narrow
   column on a desktop has the same problem a phone does. */
@container (max-width: 420px) {
  .ec-actions > .ec-button { flex: 1 1 100%; }
  /* Keep the two native pickers the same width on phones. The time control owns the GMT label,
     so its input can shrink inside the track without pushing the label below the picker. */
  .ec-date-time { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@container (max-width: 300px) {
  /* Native Safari controls need more room than a very narrow embed can provide. Stacking here
     avoids the calendar/clock button overlap while preserving the compact row everywhere else. */
  .ec-date-time { grid-template-columns: minmax(0, 1fr); }
}
@container (min-width: 720px) {
  /* 1.4fr/1fr, not 1fr/0.8fr: the form is the work, the panels are reference. This keeps the
     inputs near 480px — comfortable to scan — instead of stretching them to fill. */
  .ec-root[data-preview='on'] { grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr); }
}
`;

/** Injects the stylesheet once per document — safe to call from every mount. */
export function ensureEventCreatorStyles(doc: Document = document): void {
  const id = 'ec-styles';
  if (doc.getElementById(id)) return;
  const style = doc.createElement('style');
  style.id = id;
  style.textContent = EVENT_CREATOR_CSS;
  doc.head.appendChild(style);

  if (supportsContainerQueries()) {
    const modern = doc.createElement('style');
    modern.id = 'ec-styles-container';
    modern.textContent = EVENT_CREATOR_CONTAINER_CSS;
    doc.head.appendChild(modern);
  }
}

function supportsContainerQueries(): boolean {
  try {
    return typeof CSS !== 'undefined' && CSS.supports('container-type: inline-size');
  } catch {
    return false;
  }
}
